import HrUnavailability from "app/models/hrUnavailability"
import { UserMonth } from "app/redux/absencesTable/absencesTable.reducer"
import { createSelector } from "reselect"
import State from "../state"

export const getAbsencesTableData = (state: State) => state.absencesTable.data

export const getSelectedDays = createSelector(getAbsencesTableData, (data): UserMonth[] =>
  data.reduce((result: UserMonth[], userMonth: UserMonth) => {
    const selectedAbsences = userMonth.absences.filter(absence => absence.selected)
    if (selectedAbsences.length > 0) {
      return [...result, { user: userMonth.user, absences: selectedAbsences }]
    }
    return result
  }, []),
)

export const selectedDaysAreFromSameUserId = createSelector(getSelectedDays, selectedDays => {
  const id = selectedDays.length && selectedDays[0].user.id
  return selectedDays.every(s => s.user.id === id)
})

export const getDaysWithoutHrUnavailabilitiesAndGeneralleaves = createSelector(getSelectedDays, selectedDays =>
  selectedDays.reduce((result: UserMonth[], userMonth: UserMonth) => {
    const withoutFullDaysAvailabilities = userMonth.absences.filter(
      absence => absence.hrUnavailabilities.length === 0 && absence.hrGeneralLeaves.length === 0,
    )
    if (withoutFullDaysAvailabilities.length > 0) {
      return [...result, { user: userMonth.user, absences: withoutFullDaysAvailabilities }]
    }
    return result
  }, []),
)

export const getDaysWithHrUnavailabilitiesOrHrGeneralLeaves = createSelector(getSelectedDays, selectedDays =>
  selectedDays.reduce((result: UserMonth[], userMonth: UserMonth) => {
    const withDaysHrUnavailabilitiesOrHrGeneralLeaves = userMonth.absences.filter(
      absence => absence.hrUnavailabilities.length > 0 || absence.hrGeneralLeaves.length > 0,
    )
    if (withDaysHrUnavailabilitiesOrHrGeneralLeaves.length > 0) {
      return [...result, { user: userMonth.user, absences: withDaysHrUnavailabilitiesOrHrGeneralLeaves }]
    }
    return result
  }, []),
)

export const getDaysWithoutFullDayHrUnavailabilitiesOrHrGeneralLeaves = createSelector(
  getDaysWithHrUnavailabilitiesOrHrGeneralLeaves,
  selectedDays =>
    selectedDays.reduce((result: UserMonth[], userMonth: UserMonth) => {
      const withouthFullDayHrUnavailabilitiesAndHrGeneralLeave = userMonth.absences.filter(absence => {
        const hrUnavailabilities = unavailabilitiesSortingHelper(absence.hrUnavailabilities)
        const firstUnavailability = hrUnavailabilities[0]
        const lastUnavailability = hrUnavailabilities[hrUnavailabilities.length - 1]
        return (
          !absence.hrGeneralLeaves.length &&
          (firstUnavailability?.start_time !== "00:00:00" || lastUnavailability?.end_time !== "00:00:00")
        )
      })
      if (withouthFullDayHrUnavailabilitiesAndHrGeneralLeave.length > 0) {
        return [...result, { user: userMonth.user, absences: withouthFullDayHrUnavailabilitiesAndHrGeneralLeave }]
      }
      return result
    }, []),
)

export const getSelectedDaysWithoutFullDayHrUnavailabilitiesAndHrGeneralLeaves = createSelector(
  getSelectedDays,
  selectedDays =>
    selectedDays.reduce((result: UserMonth[], userMonth: UserMonth) => {
      const withouthFullDayHrUnavailabilities = userMonth.absences.filter(absence => {
        const unavailabilities = unavailabilitiesSortingHelper(absence.hrUnavailabilities)
        const firstUnavailability = unavailabilities[0]
        const lastUnavailability = unavailabilities[unavailabilities.length - 1]
        return (
          !absence.hrGeneralLeaves.length &&
          (firstUnavailability?.start_time !== "00:00" || lastUnavailability?.end_time !== "00:00")
        )
      })
      if (withouthFullDayHrUnavailabilities.length > 0) {
        return [...result, { user: userMonth.user, absences: withouthFullDayHrUnavailabilities }]
      }
      return result
    }, []),
)

export const getDaysWithFullDayHrUnavailabilitiesOrHrGeneralLeaves = createSelector(
  getDaysWithHrUnavailabilitiesOrHrGeneralLeaves,
  selectedDays =>
    selectedDays.reduce((result: UserMonth[], userMonth: UserMonth) => {
      const withFullDayUnavailabilities = userMonth.absences.filter(absence => {
        const unavailabilities = unavailabilitiesSortingHelper(absence.hrUnavailabilities)
        const firstUnavailability = unavailabilities[0]
        const lastUnavailability = unavailabilities[unavailabilities.length - 1]
        return (
          absence.hrGeneralLeaves.length ||
          (firstUnavailability?.start_time === "00:00:00" && lastUnavailability?.end_time === "00:00:00")
        )
      })
      if (withFullDayUnavailabilities.length > 0) {
        return [...result, { user: userMonth.user, absences: withFullDayUnavailabilities }]
      }
      return result
    }, []),
)

export const getSelectedDaysToAdd = createSelector(
  getSelectedDaysWithoutFullDayHrUnavailabilitiesAndHrGeneralLeaves,
  (daysWithoutFullDayUnavailabilities: UserMonth[]) => {
    if (daysWithoutFullDayUnavailabilities.length > 1) {
      return daysWithoutFullDayUnavailabilities.filter((userMonth: UserMonth) =>
        userMonth.absences.filter(
          absence => absence.hrUnavailabilities.length === 0 && absence.hrGeneralLeaves.length === 0,
        ),
      )
    }
    return daysWithoutFullDayUnavailabilities
  },
)

export const getHrUnavailabilitiesFromSelectedDays = createSelector(
  getDaysWithHrUnavailabilitiesOrHrGeneralLeaves,
  daysWithHrUnavailabilitiesOrHrGeneralLeaves =>
    daysWithHrUnavailabilitiesOrHrGeneralLeaves.reduce((result: HrUnavailability[], userMonth: UserMonth) => {
      const absencesWithHrUnavailabilities = userMonth.absences.filter(absence => absence.hrUnavailabilities.length > 0)
      const hrUnavailabilities = absencesWithHrUnavailabilities.reduce(
        (hrUnavailabilitiesResult: HrUnavailability[], absence) => [
          ...hrUnavailabilitiesResult,
          ...absence.hrUnavailabilities,
        ],
        [],
      )
      return [...result, ...hrUnavailabilities]
    }, []),
)

export const getClickedRow = (state: State) => state.absencesTable.clickedRow

export const getParams = (state: State) => state.absencesTable.params

export const getUserParams = (state: State) => state.absencesTable.userParams

export const isLoading = (state: State) => state.absencesTable.loading

export const getDate = (state: State) => state.absencesTable.date

const unavailabilitiesSortingHelper = (unavail: HrUnavailability[]) =>
  [...unavail].sort((a, b) => {
    const nameA = a.start_time.toUpperCase()
    const nameB = b.start_time.toUpperCase()
    if (nameA < nameB) {
      return -1
    }
    if (nameA > nameB) {
      return 1
    }
    return 0
  })
